import { useState } from 'react';
import { useGetProjectsQuery } from 'store/api';
import {
  HeaderShort,
  MailMenuCell,
  RowCell,
  TabIndexTable,
  AlertDialog,
  BackdropLoading,
} from '@centerpiece/components';
import { useNavigate } from 'react-router-dom';
import { Button, Container, Tooltip } from '@mui/material';
import {
  Schema,
  SettingsOutlined,
  PersonOutlineOutlined,
  Logout,
  ListOutlined,
  ArrowBack,
} from '@mui/icons-material';
import { useLogout } from 'utils/logout.utils';
import { SettingsTabDrawer } from 'drawers/SettingsTabDrawer';
import { AccountTabDrawer } from 'drawers/AccountTabDrawer';
import { ProjectTabDrawer } from 'drawers/ProjectTabDrawer';
import { CreateTemplateDialogue } from 'dialogs/CreateTemplateDialogue';

interface AlertDetails {
  title: string;
  content: string;
  onConfirm: Function;
}

export const TemplateIndexPage = () => {
  const navigate = useNavigate();
  const onLogout = useLogout();
  const [openDialog, setOpenDialog] = useState<string>();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [project, setProject] = useState<number>(0);
  const [filter, setFilter] = useState<string>('Active');
  const [openAccountDrawer, setOpenAccountDrawer] = useState<boolean>(false);
  const [activeItem /*, setActiveItem*/] = useState<any>();
  const [alertDetails, setAlertDetails] = useState<AlertDetails>();

  const {
    data: projects = [],
    isLoading: projectListLoading,
    isFetching: projectListFetching,
  } = useGetProjectsQuery({
    params:
      filter === 'Archived'
        ? { optimized: 'true', archived: true, template: true }
        : { optimized: 'true', template: true },
  });

  return (
    <div style={{ height: '100%' }}>
      <AlertDialog
        open={!!alertDetails}
        title={alertDetails?.title || ''}
        content={alertDetails?.content || ''}
        onClose={() => {
          setAlertDetails(undefined);
        }}
        onConfirm={() => {
          alertDetails?.onConfirm && alertDetails.onConfirm();
          setAlertDetails(undefined);
        }}
      />
      <AccountTabDrawer
        open={openAccountDrawer}
        onClose={() => {
          setOpenAccountDrawer(false);
        }}
      />

      {!activeItem && (
        <SettingsTabDrawer
          open={openDrawer}
          onClose={() => {
            setOpenDrawer(false);
          }}
        />
      )}
      {project > 0 && (
        <ProjectTabDrawer
          template={true}
          open={project > 0}
          initialTab={'info'}
          previewTab={true}
          id={project}
          onClose={() => {
            setProject(0);
          }}
        />
      )}
      <CreateTemplateDialogue
        open={!!openDialog}
        category={openDialog}
        close={() => setOpenDialog(undefined)}
      />
      <HeaderShort
        tooltip="Menu"
        onLogoClick={() => navigate('/')}
        menuItems={[
          {
            icon: SettingsOutlined,
            label: 'Settings',
            onClick: () => setOpenDrawer(true),
            type: 'buttonitem',
          },
          {
            icon: PersonOutlineOutlined,
            label: 'Account',
            onClick: () => setOpenAccountDrawer(true),
            type: 'buttonitem',
          },
          // {
          //   icon: SlideshowOutlined, label: 'Tutorial', onClick: () => setState({
          //     run: true,
          //     steps: indexSteps(),
          //     alias: 'TemplateIndexPage',
          //     stepIndex: 0,
          //     callback: (data: any) => {
          //       const { action, index, status, type, lifecycle } = data;
          //       if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
          //         if (action == ACTIONS.NEXT && index === 5 && lifecycle === LIFECYCLE.COMPLETE) {
          //           // Need to set our running state to false, so we can restart if we click start again.
          //           setState(currState => ({ ...currState, tourActive: true, run: false }))
          //           setOpenDialog('FES')
          //         } else if (action == ACTIONS.NEXT && index === 6 && lifecycle === LIFECYCLE.COMPLETE) {
          //           // Need to set our running state to false, so we can restart if we click start again.
          //           setOpenDialog(undefined)
          //           setState(currState => ({ ...currState, tourActive: true, run: false }))
          //           setOpenDrawer(true)
          //         } else if (action == ACTIONS.NEXT && index === 7 && lifecycle === LIFECYCLE.COMPLETE) {
          //           // Need to set our running state to false, so we can restart if we click start again.
          //           setState(currState => ({ ...currState, tourActive: true, run: false }))
          //         } else {
          //           const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
          //           setState(currState => ({ ...currState, stepIndex: index + (action === ACTIONS.PREV ? -1 : 1) }));
          //         }
          //       } else if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
          //         // Need to set our running state to false, so we can restart if we click start again.
          //         setOpenDialog(undefined)
          //         setOpenDrawer(false)
          //         setState({ run: false, stepIndex: 0 });
          //       }
          //     }
          //   }), type: 'buttonitem'
          // },
          {
            icon: ListOutlined,
            label: 'Projects',
            onClick: () => navigate('/'),
          },
          { icon: Logout, label: 'Logout', onClick: () => onLogout() },
        ]}
        moreIconButtonProps={{ size: 'medium' }}
      />
      <Container maxWidth={'xl'} sx={{ pt: { xs: 2, md: 6 } }}>
        {projectListLoading && <BackdropLoading />}
        <Tooltip title={'Back to project index'}>
          <Button
            color="secondary"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ pl: 0 }}
          >
            Back to project index page
          </Button>
        </Tooltip>
        <TabIndexTable
          tables={[
            {
              title: 'Project Templates',
              key: 'templates',
              visible: true,
              filters: [
                {
                  options: ['Active', 'Archived'],
                  defaultOption: 'Active',
                  onChange: (event, newValue) => {
                    setFilter(newValue || 'Active');
                  },
                  loading: projectListFetching,
                },
              ],
              actions: [
                {
                  name: 'Create Project Template',
                  icon: <Schema />,
                  onClick: () => setOpenDialog('FES'),
                },
              ],
              onRowClick: (event, row) =>
                navigate(`/project_template/${row?.id}`),
              // isLoading: projectListLoading,
              defaultOrder: 'asc',
              defaultOrderBy: 'title',
              hoverCell: true,
              headCells: [
                {
                  name: 'title',
                  label: 'Title',
                },
                {
                  name: 'details',
                  label: 'Details',
                },
                {
                  name: 'msgs',
                  label: '',
                  align: 'right',
                  padding: 'checkbox',
                  component: MailMenuCell,
                },
              ],
              rows: projects
                ? (projects?.map(p => ({
                    ...p,
                    msgs: { infoClick: () => setProject(p.id) },
                  })) as RowCell[])
                : ([] as RowCell[]),
            },
          ]}
        />
      </Container>
    </div>
  );
};

// eslint-disable-next-line
function waitForElm(selector: string) {
  return new Promise(resolve => {
    if (document.getElementById(selector)) {
      return resolve(document.getElementById(selector));
    }

    const observer = new MutationObserver(mutations => {
      if (document.getElementById(selector)) {
        resolve(document.getElementById(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}
